import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { useHotel } from "@/lib/hotel";
import { TID } from "@/constants/testIds/aura";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ROLE_WORKSPACES, WORKSPACE_BY_SLUG, DEFAULT_WORKSPACE_SLUG } from "@/lib/roleWorkspaces";
import { useServerConfig } from "@/lib/serverConfig";
import logoMark from "@/assets/logo-allround.png";
import {
  ArrowLeft, ArrowRight, Lock, KeyRound,
  User, Stethoscope, Building2, ShieldCheck, Pill, FlaskConical, Truck, Beaker,
} from "lucide-react";

const ICONS = { User, Stethoscope, Building2, ShieldCheck, Pill, FlaskConical, Truck, Beaker };

const roleRoute = (u, opts = {}) => {
  // A patient who arrived via a QR scan (RoomServiceSession pending) is
  // routed to /stay/confirm so we can attach a temporary HotelStayContext
  // before any clinical activity begins.
  if (u.role === "patient" && opts.pendingRoomSession) return "/stay/confirm";
  if (u.role === "doctor") return "/doctor";
  if (u.role === "pharmacy") return "/pharmacy/orders";
  if (u.role === "laboratory") return "/laboratory/orders";
  if (u.role === "courier") return "/courier";
  if (u.role === "specimen_collector") return "/collector";
  if (u.role === "platform_admin") return "/platform";
  if (u.role === "insurance_admin" || u.role === "hmo_admin" || u.role === "corporate_admin") return "/partner/home";
  if (u.role === "hotel_manager") return "/hotel/dashboard";
  if (u.role === "hotel_staff") return "/hotel/dashboard";
  if (u.role === "patient") return "/guest";
  return "/emergency";
};

const DEMO_PASSWORD = "Demo123!";
// The demo shortcut is a QA / preview-only helper. It needs BOTH the build
// flag AND the backend reporting DEMO_MODE, so a production deployment never
// shows it even if the flag is left on in the build.
const DEMO_BUILD_FLAG = process.env.REACT_APP_ENABLE_DEMO_LOGIN === "true";

export default function Login() {
  const { login, mfaChallenge, verifyMfa, cancelMfa, pwChangeChallenge, changePassword, cancelPasswordChange } = useAuth();
  const { ctx } = useHotel();
  const nav = useNavigate();
  const { t } = useTranslation();
  const { role: slugParam } = useParams();
  const workspace = WORKSPACE_BY_SLUG[slugParam] || WORKSPACE_BY_SLUG[DEFAULT_WORKSPACE_SLUG];
  const Icon = ICONS[workspace.icon] || User;

  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const serverCfg = useServerConfig();
  const demoLoginEnabled = DEMO_BUILD_FLAG && serverCfg?.demo_mode === true;

  const pendingRoomSession = !!ctx?.session_id;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, pw);
      if (result?.mfa_required) return; // fall through — MFA UI now visible
      if (result?.must_change_password) return; // forced password change UI now visible
      toast.success("Welcome back");
      nav(roleRoute(result, { pendingRoomSession }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await verifyMfa(mfaCode);
      if (u?.must_change_password) return; // forced password change UI now visible
      toast.success("Welcome back");
      nav(roleRoute(u, { pendingRoomSession }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "MFA failed");
    } finally {
      setLoading(false);
    }
  };

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error("The two passwords don't match");
      return;
    }
    setLoading(true);
    try {
      const u = await changePassword(pw, newPw);
      toast.success("Password updated — you're signed in");
      nav(roleRoute(u, { pendingRoomSession }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update the password");
    } finally {
      setLoading(false);
    }
  };

  const signInAsDemo = async () => {
    setDemoLoading(true);
    setLoading(true);
    try {
      const result = await login(workspace.demoEmail, DEMO_PASSWORD);
      if (result?.mfa_required) {
        toast.info("MFA is enabled on this account — enter your code.");
        return;
      }
      toast.success(`Signed in as ${workspace.demoEmail}`);
      nav(roleRoute(result, { pendingRoomSession }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Demo login failed — is the seed loaded?");
    } finally {
      setLoading(false);
      setDemoLoading(false);
    }
  };

  const startEmergentSso = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH.
    sessionStorage.setItem("aura_sso_role_hint", "hotel_manager");
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-warm" data-testid="login-page" data-role={workspace.slug}>
      {/* Top bar */}
      <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-1 text-stone-500 text-sm hover:text-forest" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> {t("common.back")}
        </Link>
        <LanguageSwitcher />
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-20 grid lg:grid-cols-2 gap-10 items-start">
        {/* Left — role hero */}
        <div className="fade-up hidden lg:block">
          <div className="relative aspect-[4/5] rounded-[1.75rem] overflow-hidden border border-[rgba(58,81,65,0.14)]">
            <img src={workspace.image} alt={workspace.label} className="absolute inset-0 h-full w-full object-cover" loading="eager" />
            <div className="absolute inset-0 bg-gradient-to-t from-[rgba(35,52,42,0.82)] via-[rgba(35,52,42,0.15)] to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="chip-white inline-flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-widest font-semibold">
                <Icon className="h-3 w-3" /> {workspace.label} sign-in
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl leading-tight mt-4 text-cream">{workspace.tagline}</h1>
              <p className="text-cream/80 text-sm mt-3 max-w-md leading-relaxed">{workspace.description}</p>
            </div>
          </div>

          {/* Other workspaces */}
          <div className="mt-6">
            <div className="eyebrow mb-3">Other workspaces</div>
            <div className="flex flex-wrap gap-2">
              {ROLE_WORKSPACES.filter((w) => w.slug !== workspace.slug).map((w) => (
                <Link
                  key={w.slug}
                  to={`/login/${w.slug}`}
                  className="chip text-xs hover:border-forest hover:text-forest transition"
                  data-testid={`login-switch-${w.slug}`}
                >
                  {w.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Right — form card */}
        <div className="fade-up lg:pt-6" style={{ animationDelay: "80ms" }}>
          <div className="flex items-center gap-2 mb-6 lg:hidden">
            <img src={logoMark} alt="AllRound" className="h-8 w-8 rounded-full object-cover" />
            <span className="font-serif text-xl text-forest">AllRound</span>
          </div>

          <div className="card-lux p-7 sm:p-8" data-testid="login-card">
            <div className="inline-flex items-center gap-2 chip text-xs mb-5" data-testid="login-role-chip">
              <Icon className="h-3.5 w-3.5" /> {workspace.org} · {workspace.authNote}
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl text-stone-900">
              {t("common.signIn")}, {workspace.label.toLowerCase()}.
            </h2>
            <p className="text-stone-500 text-sm mt-2">{t("auth.loginSubtitle")}</p>

            <div className="mt-6">
              {pwChangeChallenge ? (
                <form onSubmit={submitPasswordChange} className="space-y-4" data-testid="force-password-change-form">
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-forest" />
                    <div className="text-xs uppercase tracking-widest text-forest font-semibold">
                      Set a permanent password
                    </div>
                  </div>
                  <p className="text-sm text-stone-600" data-testid="force-password-change-reason">
                    {pwChangeChallenge.email} is still using the temporary password issued by your
                    administrator. Choose a new one to activate the account — nothing else is
                    accessible until you do.
                  </p>
                  <Field label="New password">
                    <input
                      autoFocus type="password" required minLength={8} value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      className="h-12 w-full rounded-full border border-[rgba(58,81,65,0.16)] px-5 bg-white"
                      placeholder="••••••••"
                      data-testid="new-password-input"
                    />
                  </Field>
                  <Field label="Confirm new password">
                    <input
                      type="password" required minLength={8} value={confirmPw}
                      onChange={(e) => setConfirmPw(e.target.value)}
                      className="h-12 w-full rounded-full border border-[rgba(58,81,65,0.16)] px-5 bg-white"
                      placeholder="••••••••"
                      data-testid="confirm-password-input"
                    />
                  </Field>
                  <p className="text-xs text-stone-500">
                    At least 8 characters, and for clinical or admin accounts three of:
                    lowercase, uppercase, digit, symbol.
                  </p>
                  <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="force-password-change-submit-btn">
                    {loading ? t("common.loading") : (<>Save and continue <ArrowRight className="h-4 w-4" /></>)}
                  </button>
                  <button
                    type="button"
                    onClick={() => { cancelPasswordChange(); setNewPw(""); setConfirmPw(""); setPw(""); }}
                    className="text-xs text-stone-500 w-full text-center hover:text-forest transition-colors"
                    data-testid="force-password-change-cancel-btn"
                  >
                    Cancel
                  </button>
                </form>
              ) : mfaChallenge ? (
                <form onSubmit={submitMfa} className="space-y-4" data-testid="mfa-form">
                  <div className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-forest" />
                    <div className="text-xs uppercase tracking-widest text-forest font-semibold">
                      Two-factor authentication
                    </div>
                  </div>
                  <p className="text-sm text-stone-600">
                    Enter the 6-digit code from your authenticator app, or one of your one-time backup codes.
                  </p>
                  <input
                    autoFocus
                    inputMode="numeric"
                    pattern="[0-9\-]*"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    className="h-14 w-full rounded-2xl border border-[rgba(58,81,65,0.16)] px-5 bg-white text-2xl tracking-widest text-center font-mono"
                    placeholder="123 456"
                    data-testid="mfa-code-input"
                  />
                  <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="mfa-submit-btn">
                    {loading ? t("common.loading") : (<>Verify <ArrowRight className="h-4 w-4" /></>)}
                  </button>
                  <button
                    type="button"
                    onClick={cancelMfa}
                    className="text-xs text-stone-500 w-full text-center hover:text-forest transition-colors"
                    data-testid="mfa-cancel-btn"
                  >
                    Cancel
                  </button>
                </form>
              ) : (
                <>
                  <form onSubmit={submit} className="space-y-4">
                    <Field label="Work email">
                      <input
                        type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        className="h-12 w-full rounded-full border border-[rgba(58,81,65,0.16)] px-5 bg-white"
                        data-testid={TID.emailInput}
                        placeholder="you@example.com"
                      />
                    </Field>
                    <Field label={t("auth.password")}>
                      <input
                        type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
                        className="h-12 w-full rounded-full border border-[rgba(58,81,65,0.16)] px-5 bg-white"
                        data-testid={TID.passwordInput}
                        placeholder="••••••••"
                      />
                    </Field>
                    <button type="submit" className="btn-primary w-full" disabled={loading} data-testid={TID.submitBtn}>
                      {loading ? t("common.loading") : (<>{t("auth.loginBtn")} <ArrowRight className="h-4 w-4" /></>)}
                    </button>
                  </form>

                  {demoLoginEnabled && (
                    <button
                      type="button"
                      onClick={signInAsDemo}
                      disabled={loading}
                      className="btn-secondary w-full mt-3 disabled:opacity-60"
                      data-testid="demo-login-btn"
                    >
                      {demoLoading ? "Signing in…" : "Use demo credentials"}
                    </button>
                  )}

                  {serverCfg?.needs_setup && (
                    <Link
                      to="/setup"
                      className="btn-secondary w-full mt-3"
                      data-testid="login-to-setup"
                    >
                      Set up this platform
                    </Link>
                  )}

                  <div className="my-5 flex items-center gap-3">
                    <div className="flex-1 h-px bg-[rgba(58,81,65,0.14)]" />
                    <div className="text-[11px] uppercase tracking-widest text-stone-400">or</div>
                    <div className="flex-1 h-px bg-[rgba(58,81,65,0.14)]" />
                  </div>

                  <button
                    type="button"
                    onClick={startEmergentSso}
                    className="btn-secondary w-full"
                    data-testid="sso-emergent-btn"
                  >
                    Continue with Google
                  </button>

                  <p className="text-sm text-stone-500 text-center pt-5">
                    <Link to="/register" className="link-under font-medium" data-testid={TID.toRegister}>
                      {t("auth.registerLink")}
                    </Link>
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Mobile-only other workspaces */}
          <div className="mt-6 lg:hidden">
            <div className="eyebrow mb-3">Other workspaces</div>
            <div className="flex flex-wrap gap-2">
              {ROLE_WORKSPACES.filter((w) => w.slug !== workspace.slug).map((w) => (
                <Link key={w.slug} to={`/login/${w.slug}`} className="chip text-xs" data-testid={`login-switch-mobile-${w.slug}`}>
                  {w.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-stone-500 mb-2 font-semibold">{label}</div>
      {children}
    </label>
  );
}
